import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { QueryError } from '@atlassian/jira-business-error-handling/src/utils/query-error/index.tsx';
import { TabsSkeleton } from '@atlassian/jira-horizontal-nav-common/src/ui/horizontal-nav-skeleton/index.tsx';
import type { RelayAGGError } from '@atlassian/jira-horizontal-nav-error-boundary/src/ui/agg-error-boundary/utils.tsx';
import { HANDLED_STATUS_CODES } from '@atlassian/jira-horizontal-nav-common/src/constants.tsx';
import { showFlag } from '@atlassian/jira-flags/src/services/index.tsx';
import { CONTAINER } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-layer.tsx';
import { useShortcutsData } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/shortcuts/shortcuts-data/index.tsx';
import { ShortcutsContainer } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/shortcuts/state-container/index.tsx';
import { PROJECT } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/container-type.tsx';
import { TAB } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	fireTrackAnalytics,
	SCREEN,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import {
	ContainerNavigationTabs,
	type Props as ContainerNavigationTabsProps,
} from '@atlassian/jira-horizontal-nav-tabs/src/ui/ContainerNavigationTabs.tsx';
import { getFlagDetails } from '@atlassian/jira-horizontal-nav-common/src/common/get-flag-details/getFlagDetails.tsx';
import { PagesSpotlightEntrypointContainer } from '@atlassian/jira-pages-spotlight-nav4/src/ui/PagesSpotlightEntrypointContainer.tsx';
import { PagesSpotlightEntrypointContainer as PagesSpotlightSpaceConnectedEntrypointContainer } from '@atlassian/jira-pages-spotlight-space-connected/src/ui/PagesSpotlightEntrypointContainer.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	getProjectAttributes,
	useRelativeURLFromLocation,
	getOriginAnalyticsAttributes,
} from '@atlassian/jira-platform-router-origin-tracing/src/common/utils.tsx';
import { JIRA_TABS } from '../../constants.tsx';
import type { TabsContent_query$key } from './__generated__/TabsContent_query.graphql';
import type { TabsContent_project$key } from './__generated__/TabsContent_project.graphql';
import messages from './messages.tsx';

type Props = {
	queryFragment: TabsContent_query$key;
	projectFragment?: TabsContent_project$key | null;
};

const TabsContent = ({ queryFragment, projectFragment = null }: Props): React.JSX.Element => {
	const projectNavigationGalaxia = expVal('project-terminology-refresh', 'isEnabled', false)
		? messages.projectNavigationGalaxia
		: messages.projectNavigation;
	const { formatMessage } = useIntl();
	const hasFoundErrorRef = useRef<boolean>(false);
	const urlFromLocation = useRelativeURLFromLocation();

	const data = useFragment(
		graphql`
			fragment TabsContent_query on Query {
				jira @required(action: THROW) {
					containerNavigation(input: $containerNavigationInput)
						@required(action: THROW)
						@optIn(to: "JiraContainerNavigation") {
						...ContainerNavigationTabs_container
						... on JiraContainerNavigation {
							navigationItems(first: $first) {
								edges {
									__typename
									node @required(action: THROW) {
										id @required(action: THROW)
									}
								}
								errors {
									# eslint-disable-next-line @atlassian/relay/unused-fields
									message
									# eslint-disable-next-line @atlassian/relay/unused-fields
									extensions {
										statusCode
									}
								}
							}
						}
						__typename
						... on QueryError {
							# eslint-disable-next-line @atlassian/relay/unused-fields
							message
							# eslint-disable-next-line @atlassian/relay/unused-fields
							extensions {
								statusCode
							}
						}
					}
				}
			}
		`,
		queryFragment,
	);

	const projectData = useFragment(
		graphql`
			fragment TabsContent_project on JiraProject {
				projectType
				projectId
			}
		`,
		projectFragment,
	);

	const { data: shortcutsData, loading } = useShortcutsData();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleError = useCallback(
		(error: Error | QueryError | RelayAGGError) => {
			if (fg('jira_client_side_error_handled_track_event')) {
				if (!hasFoundErrorRef.current) {
					hasFoundErrorRef.current = true;
					const statusCode = error && 'statusCode' in error ? Number(error.statusCode) : 0;
					const flagDetails = getFlagDetails(statusCode);

					showFlag(flagDetails);

					if (HANDLED_STATUS_CODES.includes(statusCode)) {
						fireTrackAnalytics(createAnalyticsEvent({}), 'clientSideError handled', {
							statusCode,
							// It's important to get the relative url from a hook rather than
							// by calling getRouterState at this point because any redirections
							// that occur before this callback is run may cause the atlOrigin
							// query param to be lost.
							originTrace: getOriginAnalyticsAttributes(urlFromLocation),
							...getProjectAttributes(urlFromLocation),
						});

						return;
					}

					throw error;
				}
			} else {
				const statusCode = error && 'statusCode' in error ? Number(error.statusCode) : 0;
				const flagDetails = getFlagDetails(statusCode);

				showFlag(flagDetails);

				if (HANDLED_STATUS_CODES.includes(statusCode)) {
					return;
				}

				throw error;
			}
		},
		[createAnalyticsEvent, urlFromLocation],
	);

	const containerNavigation =
		data.jira.containerNavigation?.__typename === 'JiraContainerNavigation'
			? data.jira.containerNavigation
			: null;
	const navigationItems = containerNavigation ? containerNavigation?.navigationItems : null;

	useEffect(() => {
		const containerNavigationQueryError =
			data.jira.containerNavigation?.__typename === 'QueryError'
				? data.jira.containerNavigation
				: null;

		if (navigationItems?.errors && navigationItems?.errors?.length > 0) {
			const error = navigationItems.errors[0];
			handleError(new QueryError(error));
		} else if (containerNavigationQueryError) {
			handleError(new QueryError(containerNavigationQueryError));
		} else if (navigationItems?.edges == null) {
			handleError(new Error('navigationItems.edges failed to load'));
		} else {
			// reset error state
			hasFoundErrorRef.current = false;
		}
	}, [
		containerNavigation?.navigationItems,
		data,
		handleError,
		navigationItems?.edges,
		navigationItems?.errors,
	]);

	const attributes = useMemo(
		() => ({
			projectType: (projectData?.projectType ?? '').toLowerCase(),
			navigationContainer: TAB,
			navigationLayer: CONTAINER,
			tabIds: navigationItems?.edges?.map((edge) => edge?.node?.id) ?? [],
		}),
		[projectData?.projectType, navigationItems?.edges],
	);

	const renderWithTab = useCallback<NonNullable<ContainerNavigationTabsProps['renderWithTab']>>(
		(tabData) =>
			tabData.typeKey === 'PAGES' ? (
				<>
					<PagesSpotlightEntrypointContainer tabData={tabData} />
					<PagesSpotlightSpaceConnectedEntrypointContainer
						tabData={tabData}
						projectId={projectData?.projectId}
					/>
				</>
			) : null,
		[projectData?.projectId],
	);

	return containerNavigation &&
		navigationItems &&
		!navigationItems.errors &&
		navigationItems?.edges ? (
		<ContextualAnalyticsData
			sourceType={SCREEN}
			sourceName="horizontalNavJiraTabsContent"
			containerType={PROJECT}
			containerId={projectData?.projectId ?? ''}
			attributes={attributes}
		>
			<ShortcutsContainer data={shortcutsData} loading={loading} isGlobal>
				<ContainerNavigationTabs
					containerFragment={data.jira.containerNavigation}
					tabsDescriptor={JIRA_TABS}
					label={formatMessage(projectNavigationGalaxia)}
					renderWithTab={renderWithTab}
				/>
			</ShortcutsContainer>
			<FireScreenAnalytics key={projectData?.projectId ?? ''} />
		</ContextualAnalyticsData>
	) : (
		<TabsSkeleton />
	);
};

export default memo(TabsContent);
